'use client';

import { FC, useEffect, useState } from 'react';
import Spreadsheet from 'react-spreadsheet';
import styles from './XLSViewer.module.css';
import * as XLSX from 'xlsx';
import { Flex, Text } from '@/_components';
import { XLSViewerProps } from './XLSViewer.types';

export const XLSViewer: FC<XLSViewerProps> = ({ url }) => {
  const [data, setData] = useState<Array<Array<{ value: string }>> | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);
  const [activeSheet, setActiveSheet] = useState<string>('');

  const loadSheetData = (wb: XLSX.WorkBook, sheetName: string) => {
    const worksheet = wb.Sheets[sheetName];
    if (!worksheet) {
      setData([]);
      return;
    }

    const jsonData = XLSX.utils.sheet_to_json(worksheet, {
      header: 1,
      raw: false,
      dateNF: 'yyyy-mm-dd',
      defval: '',
    });

    if (jsonData.length === 0) {
      setData([]);
      return;
    }

    const maxCols = Math.max(...(jsonData as any[][]).map((row) => row.length));

    const formattedData = (jsonData as any[][]).map((row: any[]) => {
      const paddedRow = [...row];
      while (paddedRow.length < maxCols) {
        paddedRow.push('');
      }
      return paddedRow.map((cell: any) => ({
        value: cell !== null && cell !== undefined ? String(cell) : '',
      }));
    });

    setData(formattedData);
  };

  const handleSheetChange = (sheetName: string) => {
    if (workbook) {
      setActiveSheet(sheetName);
      loadSheetData(workbook, sheetName);
    }
  };

  useEffect(() => {
    if (!url) {
      setLoading(false);
      return;
    }

    const fetchAndParseExcel = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.statusText}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        const wb = XLSX.read(arrayBuffer, {
          type: 'array',
          cellDates: true,
          cellNF: false,
          cellText: false,
        });

        if (!wb.SheetNames.length) {
          throw new Error('No worksheets found in Excel file');
        }

        setWorkbook(wb);
        const firstSheet = wb.SheetNames[0];
        setActiveSheet(firstSheet);

        loadSheetData(wb, firstSheet);
        setLoading(false);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load Excel file',
        );
        setLoading(false);
      }
    };

    fetchAndParseExcel();
  }, [url]);

  if (!url) return null;

  if (loading) {
    return (
      <Flex className={styles.xlsViewer} isCentered={true}>
        <Text color="secondary">Loading Excel file...</Text>
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex className={styles.xlsViewer} isCentered={true}>
        <Text color="secondary">Error: {error}</Text>
      </Flex>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Flex className={styles.xlsViewer} isCentered={true}>
        <Text color="secondary">No data found in Excel file</Text>
      </Flex>
    );
  }

  return (
    <Flex isColumn={true} isFullWidth={true} className={styles.xlsViewer}>
      {workbook && workbook.SheetNames.length > 1 && (
        <div className={styles.sheetTabs}>
          {workbook.SheetNames.map((sheetName) => (
            <button
              key={sheetName}
              className={`${styles.sheetTab} ${
                activeSheet === sheetName ? styles.activeSheetTab : ''
              }`}
              onClick={() => handleSheetChange(sheetName)}
            >
              {sheetName}
            </button>
          ))}
        </div>
      )}
      <div className={styles.spreadsheetContainer}>
        <Spreadsheet data={data} />
      </div>
    </Flex>
  );
};
